import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Eye,
  FileImage,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  Star,
  X,
  XCircle,
} from 'lucide-react';
import {
  ActionMenuDivider,
  ActionMenuItem,
  ActionsDropdownMenu,
} from '../../components/MasterAdmin/deep-links/ActionsDropdownMenu';
import {
  getFeedbacks,
  updateFeedbackStatus,
  canApproveForProviderProfile,
  statusLabel,
  FeedbackRow,
  ServiceType,
  TargetType,
  ActorType,
  FeedbackStatus,
} from '../../services/MasterAdmin/feedbackService';

const SERVICE_OPTIONS: { value: '' | ServiceType; label: string }[] = [
  { value: '', label: 'All services' },
  { value: 'AMBULANCE', label: 'Ambulance' },
  { value: 'BLOOD_BANK', label: 'Blood Bank' },
  { value: 'MEDICINE', label: 'Medicine' },
  { value: 'LAB_TEST', label: 'Lab Test' },
  { value: 'CLINIC', label: 'Clinic' },
  { value: 'GENERAL_APP', label: 'General App' },
];

const TARGET_OPTIONS: { value: '' | TargetType; label: string }[] = [
  { value: '', label: 'All targets' },
  { value: 'VENDOR', label: 'Service Provider' },
  { value: 'DELIVERY_PARTNER', label: 'Delivery Partner' },
  { value: 'APP', label: 'App' },
  { value: 'SUPPORT', label: 'Support' },
];

const ACTOR_OPTIONS: { value: '' | ActorType; label: string }[] = [
  { value: '', label: 'All (User / Service Provider)' },
  { value: 'USER', label: 'User' },
  { value: 'VENDOR', label: 'Service Provider' },
];

const STATUS_OPTIONS: { value: '' | FeedbackStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function actorTypeLabel(actorType: ActorType | null | undefined) {
  if (actorType === 'USER') return 'User';
  if (actorType === 'VENDOR') return 'Service Provider';
  return '—';
}

function statusBadgeClass(status: FeedbackStatus) {
  if (status === 'APPROVED') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  }
  if (status === 'REJECTED') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
}

function getProfilePath(row: FeedbackRow): string | null {
  if (row.submitterProfilePath) return row.submitterProfilePath;
  if (row.actorType === 'USER' && row.userId) return `/users/${row.userId}`;
  if (row.actorType === 'VENDOR' && row.userId && row.vendorKind) {
    return `/vendors/${row.vendorKind}/${row.userId}`;
  }
  return null;
}

type ProfileLinkProps = {
  row: FeedbackRow;
  className?: string;
  children: React.ReactNode;
};

type FeedbackRowActionsMenuProps = {
  row: FeedbackRow;
  updating: boolean;
  onView: (row: FeedbackRow) => void;
  onApprove: (row: FeedbackRow) => void;
  onReject: (row: FeedbackRow) => void;
};

const FeedbackRowActionsMenu: React.FC<FeedbackRowActionsMenuProps> = ({
  row,
  updating,
  onView,
  onApprove,
  onReject,
}) => {
  const [open, setOpen] = useState(false);
  const showProfileActions = canApproveForProviderProfile(row);
  const canApprove = showProfileActions && row.status !== 'APPROVED';
  const canReject = showProfileActions && row.status !== 'REJECTED';

  return (
    <ActionsDropdownMenu open={open} onToggle={() => setOpen((v) => !v)} onClose={() => setOpen(false)}>
      <ActionMenuItem
        icon={<Eye className="h-4 w-4 opacity-70" />}
        label="View details"
        onClick={() => {
          setOpen(false);
          onView(row);
        }}
      />
      {showProfileActions && (canApprove || canReject) && <ActionMenuDivider />}
      {canApprove && (
        <ActionMenuItem
          icon={<CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
          label={updating ? 'Approving…' : 'Approve for profile'}
          disabled={updating}
          onClick={() => {
            setOpen(false);
            onApprove(row);
          }}
        />
      )}
      {canReject && (
        <ActionMenuItem
          icon={<XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />}
          label="Reject"
          tone="danger"
          disabled={updating}
          onClick={() => {
            setOpen(false);
            onReject(row);
          }}
        />
      )}
    </ActionsDropdownMenu>
  );
};

const ProfileLink: React.FC<ProfileLinkProps> = ({ row, className = '', children }) => {
  const path = getProfilePath(row);
  if (!path) {
    return <span className={className}>{children}</span>;
  }
  return (
    <Link
      to={path}
      className={`text-primary-600 dark:text-primary-400 hover:underline ${className}`}
    >
      {children}
    </Link>
  );
};

type DetailModalProps = {
  row: FeedbackRow;
  onClose: () => void;
  onApprove?: (row: FeedbackRow) => void;
  onReject?: (row: FeedbackRow) => void;
  updating?: boolean;
};

const DetailModal: React.FC<DetailModalProps> = ({ row, onClose, onApprove, onReject, updating = false }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileUrls = Array.isArray(row.fileUrls) ? row.fileUrls : [];
  const profilePath = getProfilePath(row);

  const isImage = (url: string) => {
    const u = url.toLowerCase();
    return /\.(jpe?g|png|gif|webp)(\?|$)/.test(u) || u.includes('firebasestorage');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-xl shadow-xl max-w-2xl w-full max-h-[min(92dvh,calc(100vh-1rem))] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between p-4 border-b dark:border-gray-700 bg-white dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Feedback details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Type</span>
              <p className="font-medium text-gray-900 dark:text-white">
                {profilePath ? (
                  <Link to={profilePath} className="text-primary-600 dark:text-primary-400 hover:underline">
                    {actorTypeLabel(row.actorType)}
                  </Link>
                ) : (
                  actorTypeLabel(row.actorType)
                )}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Name</span>
              <p className="font-medium text-gray-900 dark:text-white">
                <ProfileLink row={row}>{row.submitterName || '—'}</ProfileLink>
              </p>
            </div>
            {row.actorType === 'VENDOR' && row.vendorTypeLabel && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Provider category</span>
                <p className="font-medium text-gray-900 dark:text-white">{row.vendorTypeLabel}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 dark:text-gray-400">Phone</span>
              <p className="font-medium text-gray-900 dark:text-white">{row.submitterPhone || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Service</span>
              <p className="font-medium text-gray-900 dark:text-white">{row.serviceType || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Target</span>
              <p className="font-medium text-gray-900 dark:text-white">
                {row.targetType === 'VENDOR' ? 'Service Provider' : row.targetType || '—'}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Rating</span>
              <p className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                {row.rating} <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <p className="font-medium text-gray-900 dark:text-white">
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(row.status)}`}>
                  {statusLabel(row.status)}
                </span>
              </p>
            </div>
            {row.targetType === 'VENDOR' && row.targetId && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Provider ID</span>
                <p className="font-medium text-gray-900 dark:text-white break-all">{row.targetId}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 dark:text-gray-400">Date</span>
              <p className="font-medium text-gray-900 dark:text-white">
                {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
              </p>
            </div>
          </div>
          {row.review && (
            <div>
              <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1">
                <MessageSquare className="w-4 h-4" /> Review
              </span>
              <p className="mt-1 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {row.review}
              </p>
            </div>
          )}
          {row.ratingBreakdown && Object.keys(row.ratingBreakdown).length > 0 && (
            <div>
              <span className="text-gray-500 dark:text-gray-400 text-sm">Rating breakdown</span>
              <pre className="mt-1 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-900 dark:text-gray-100 overflow-x-auto">
                {JSON.stringify(row.ratingBreakdown, null, 2)}
              </pre>
            </div>
          )}
          {fileUrls.length > 0 && (
            <div>
              <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1">
                <FileImage className="w-4 h-4" /> Attachments ({fileUrls.length})
              </span>
              <div className="mt-2 flex flex-wrap gap-2">
                {fileUrls.map((url, i) => (
                  <div key={i} className="relative">
                    {isImage(url) ? (
                      <button
                        type="button"
                        onClick={() => setImagePreview(imagePreview === url ? null : url)}
                        className="block w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600 hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <img
                          src={url}
                          alt={`Attachment ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ) : (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 w-full max-w-[200px] px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-primary-600 dark:text-primary-400 truncate"
                      >
                        <FileImage className="w-4 h-4 flex-shrink-0" />
                        File {i + 1}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {canApproveForProviderProfile(row) && (
            <div className="flex flex-wrap gap-2 pt-2 border-t dark:border-gray-700">
              {row.status !== 'APPROVED' && onApprove && (
                <button
                  type="button"
                  disabled={updating}
                  onClick={() => onApprove(row)}
                  className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium"
                >
                  Approve for profile
                </button>
              )}
              {row.status !== 'REJECTED' && onReject && (
                <button
                  type="button"
                  disabled={updating}
                  onClick={() => onReject(row)}
                  className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium"
                >
                  Reject
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {imagePreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/80 p-2 sm:p-4"
          onClick={() => setImagePreview(null)}
        >
          <button
            type="button"
            onClick={() => setImagePreview(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={imagePreview}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const FeedbacksPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [serviceType, setServiceType] = useState<'' | ServiceType>('');
  const [targetType, setTargetType] = useState<'' | TargetType>('');
  const [status, setStatus] = useState<'' | FeedbackStatus>('');
  const [actorType, setActorType] = useState<'' | ActorType>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [selectedRow, setSelectedRow] = useState<FeedbackRow | null>(null);
  const [updatingFeedbackId, setUpdatingFeedbackId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleStatusUpdate = async (row: FeedbackRow, nextStatus: FeedbackStatus) => {
    try {
      setUpdatingFeedbackId(row.feedbackId);
      setActionError(null);
      const res = await updateFeedbackStatus(row.feedbackId, nextStatus);
      setRows((prev) =>
        prev.map((item) => (item.feedbackId === row.feedbackId ? { ...item, ...res.data } : item))
      );
      setSelectedRow((prev) =>
        prev?.feedbackId === row.feedbackId ? { ...prev, ...res.data } : prev
      );
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setActionError(e?.response?.data?.message || 'Failed to update feedback status');
    } finally {
      setUpdatingFeedbackId(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getFeedbacks({
        limit,
        offset: page * limit,
        serviceType: serviceType || undefined,
        targetType: targetType || undefined,
        status: status || undefined,
        actorType: actorType || undefined,
        search: search.trim() || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setRows(res.data);
      setTotal(res.pagination?.total ?? 0);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e?.response?.data?.message || 'Failed to load feedbacks');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, serviceType, targetType, status, actorType, search, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [search, serviceType, targetType, status, actorType, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / limit) || 1;

  const openProfile = (row: FeedbackRow) => {
    const path = getProfilePath(row);
    if (path) navigate(path);
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl">
          View all feedbacks from users and service providers. Approve user reviews about a service provider to show them on that provider&apos;s profile.
        </p>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {actionError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {actionError}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone or review..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={actorType}
              onChange={(e) => setActorType(e.target.value as '' | ActorType)}
              className="py-2 px-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm"
            >
              {ACTOR_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value as '' | ServiceType)}
              className="py-2 px-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm"
            >
              {SERVICE_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as '' | TargetType)}
              className="py-2 px-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm"
            >
              {TARGET_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as '' | FeedbackStatus)}
              className="py-2 px-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="py-2 px-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm"
              title="From date"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="py-2 px-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md text-sm"
              title="To date"
            />
          </div>
        </div>

        <div className="ma-table-scroll scrollbar-light">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Target</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rating</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Review</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600 dark:text-primary-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-red-500 dark:text-red-400 text-sm">
                    {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No feedbacks found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const profilePath = getProfilePath(row);
                  return (
                    <tr
                      key={row.feedbackId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {profilePath ? (
                          <button
                            type="button"
                            onClick={() => openProfile(row)}
                            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium hover:opacity-90 ${
                              row.actorType === 'USER'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                : row.actorType === 'VENDOR'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {actorTypeLabel(row.actorType)}
                          </button>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              row.actorType === 'USER'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                : row.actorType === 'VENDOR'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {actorTypeLabel(row.actorType)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <ProfileLink row={row} className="text-gray-900 dark:text-white font-medium">
                          {row.submitterName || '—'}
                        </ProfileLink>
                        {row.actorType === 'VENDOR' && row.vendorTypeLabel && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{row.vendorTypeLabel}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{row.submitterPhone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.serviceType}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {row.targetType === 'VENDOR' ? 'Service Provider' : row.targetType}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white flex items-center gap-1">
                        {row.rating} <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[200px] truncate" title={row.review || ''}>
                        {row.review ? `${row.review.slice(0, 50)}${row.review.length > 50 ? '…' : ''}` : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(row.status)}`}>
                          {statusLabel(row.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <FeedbackRowActionsMenu
                          row={row}
                          updating={updatingFeedbackId === row.feedbackId}
                          onView={setSelectedRow}
                          onApprove={(item) => handleStatusUpdate(item, 'APPROVED')}
                          onReject={(item) => handleStatusUpdate(item, 'REJECTED')}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 text-sm"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedRow && (
        <DetailModal
          row={selectedRow}
          onClose={() => setSelectedRow(null)}
          onApprove={(row) => handleStatusUpdate(row, 'APPROVED')}
          onReject={(row) => handleStatusUpdate(row, 'REJECTED')}
          updating={updatingFeedbackId === selectedRow.feedbackId}
        />
      )}
    </div>
  );
};

export default FeedbacksPage;
